import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Badge  from "@react/shared/ui/Badge"
import DgdModal from "./DgdModal"
import { fmt, dateFmt } from "../helpers/format"
import ActionButton from "./ActionButton"

const STATUS_TONE = { "In progress": "warning", "Confirmed": "blue", "Paid": "success" }

const ExistingBlock = ({ dgd }) => {
  const invoice = dgd.invoice
  const pdfUrl  = dgd.pdf_url
  return (
    <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
      <div className="flex items-center gap-2 shrink-0">
        <span className="w-8 h-8 grid place-items-center rounded-full bg-emerald-100 text-emerald-700">
          <FontAwesomeIcon icon="flag-checkered" />
        </span>
        <div className="flex flex-col">
          <span className="text-[10.5px] uppercase tracking-wider font-medium text-emerald-700">
            {I18n.t("projects.dgds.banner.existing_eyebrow")}
          </span>
          <a href={invoice.path} className="text-[13px] font-semibold text-slate-900 hover:text-emerald-700">
            {invoice.number || invoice.title}
          </a>
        </div>
      </div>

      <div className="hidden md:block w-px h-8 bg-emerald-200" />

      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-emerald-700/80">
          {I18n.t("projects.dgds.banner.subtotal")}
        </span>
        <span className="text-[14px] font-semibold text-slate-900 tabular-nums">{fmt(invoice.subtotal)}</span>
      </div>

      <div className="flex flex-col">
        <span className="text-[10.5px] uppercase tracking-wider font-medium text-emerald-700/80">
          {I18n.t("projects.dgds.banner.total_ttc")}
        </span>
        <span className="text-[14px] font-semibold text-slate-900 tabular-nums">{fmt(invoice.total)}</span>
      </div>

      <div className="ml-auto flex items-center gap-3">
        {invoice.status && (
          <Badge tone={STATUS_TONE[invoice.status] || "neutral"} variant="soft" size="sm">
            {invoice.status_label}
          </Badge>
        )}
        {invoice.creation_date && (
          <span className="text-[12px] text-slate-500">{dateFmt(invoice.creation_date)}</span>
        )}
        {pdfUrl && (
          <a
            href      = {pdfUrl}
            target    = "_blank"
            rel       = "noopener noreferrer"
            className = "inline-flex items-center gap-1.5 px-2.5 py-1 text-[12.5px] text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 rounded-md"
          >
            <FontAwesomeIcon icon="file-pdf" /> {I18n.t("projects.dgds.pdf.print")}
          </a>
        )}
      </div>
    </div>
  )
}

const EmptyBlock = ({ dgd, canCreate, onOpen }) => (
  <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl px-4 py-3 flex flex-wrap items-center gap-4">
    <div className="flex items-center gap-2">
      <span className="w-8 h-8 grid place-items-center rounded-full bg-slate-100 text-slate-700">
        <FontAwesomeIcon icon="flag-checkered" />
      </span>
      <div className="flex flex-col">
        <span className="text-[13px] font-semibold text-slate-900">
          {I18n.t("projects.dgds.banner.empty_title")}
        </span>
        <span className="text-[11.5px] text-slate-500">
          {I18n.t("projects.dgds.banner.empty_hint")}
        </span>
      </div>
    </div>
    {canCreate && (
      <div className="ml-auto">
        <ActionButton icon="flag-checkered" onClick={onOpen}>
          {I18n.t("projects.dgds.banner.cta")}
        </ActionButton>
      </div>
    )}
  </div>
)

const DgdBanner = ({ dgd, policies }) => {
  const [modalOpen, setModalOpen] = useState(false)
  if (!dgd) return null
  const canCreate = !!policies?.can_create_dgd
  const projectId = dgd.create_url?.match(/\/projects\/(\d+)\//)?.[1]

  return (
    <div className="px-7 mt-3">
      {dgd.exists
        ? <ExistingBlock dgd={dgd} />
        : <EmptyBlock dgd={dgd} canCreate={canCreate} onOpen={() => setModalOpen(true)} />}
      <DgdModal
        isOpen    = {modalOpen}
        projectId = {projectId}
        preview   = {dgd.preview}
        onClose   = {() => setModalOpen(false)}
      />
    </div>
  )
}

export default DgdBanner
